import { Draggable } from '@hello-pangea/dnd';
import { RefObject } from 'react';
import { DefaultOption } from 'src/components/molecules/DropdownSelect';
import { GroupType, RoutingRuleType } from 'src/graphql';
import { useTheme } from 'styled-components';
import { GroupAttributes, RoutingRule } from '..';
import { ReactStateHook } from '../RoutingRule/hooks';
import {
  PriorityContainer,
  PriorityNumber,
  RuleContainer,
  RuleJoiner,
  RuleListItem,
} from './RoutingRuleAndPriority.styles';

export type Attributes = GroupAttributes & {
  submissionMode?: DefaultOption[];
  relationshipToCompany?: DefaultOption[];
  reportedCategory?: DefaultOption[];
  systemRoles?: DefaultOption[];
  anonymous?: DefaultOption[];
  'Call.organisationHotlineNumber'?: DefaultOption[];
  coiCategory?: DefaultOption[];
  questionnaireId?: DefaultOption[];
};

export const RoutingVariant = {
  SPEAK_UP: 'speak-up',
  COI: 'coi',
} as const;
export type RoutingVariantType =
  (typeof RoutingVariant)[keyof typeof RoutingVariant];

interface Props {
  rule: Omit<RoutingRuleType, 'Author'>;
  index: number;
  count: number;
  ruleJoinerCoordinates: (number[] | null[])[];
  newRule: string | null;
  newRuleRef: RefObject<HTMLDivElement | null>;
  setRef: (element: HTMLElement | null, key: string) => void;
  groups: GroupType[];
  defaultOpen: boolean;
  attributes: Attributes;
  openRule: () => void;
  recalculateCoordinates: () => void;
  isDragDisabled: boolean;
  isDragging?: boolean;
  setHeightOfRuleBeingDragged: (heightOfRuleBeingDragged: number) => void;
  onLeavingRoutingRulePage?: (checkUnsavedRulesCallback: () => boolean) => void;
  saveRulesBeforeLeaving: boolean;
  setAllRulesAreSaved: ReactStateHook<boolean>;
  variant: RoutingVariantType;
}

export const RoutingRuleAndPriority = ({
  rule,
  index,
  count,
  ruleJoinerCoordinates,
  newRule,
  newRuleRef,
  setRef,
  groups,
  defaultOpen,
  attributes,
  openRule,
  recalculateCoordinates,
  isDragDisabled,
  isDragging,
  setHeightOfRuleBeingDragged,
  onLeavingRoutingRulePage,
  saveRulesBeforeLeaving,
  setAllRulesAreSaved,
  variant,
}: Props) => {
  const priorityForDisplay = count - rule.priority + 1;
  const [x1, x2, y1, y2] = ruleJoinerCoordinates?.[index] ?? [
    null,
    null,
    null,
    null,
  ];
  const coordinatesDefined =
    x1 != null && x2 != null && y1 != null && y2 != null;
  const theme = useTheme();
  const isVaultVariant = theme.isVaultVariant;
  const strokeColor = isVaultVariant
    ? theme.newPalette.turquoise.T300
    : theme.atlas.semantic.color.action.primary.default;
  return (
    <Draggable
      isDragDisabled={isDragDisabled}
      key={rule.id}
      draggableId={rule.id}
      index={index}
    >
      {(provided) => (
        <RuleListItem
          row={index}
          {...provided.dragHandleProps}
          {...provided.draggableProps}
          ref={(el) => {
            if (isDragging) {
              const MARGIN = 20;
              setHeightOfRuleBeingDragged((el?.offsetHeight ?? 0) + MARGIN);
            }
            provided.innerRef(el);
          }}
          id={`rule-item-${rule.id}`}
          data-testid={`rule-item-${rule.id}`}
        >
          <RuleContainer ref={rule.id === newRule ? newRuleRef : null}>
            <PriorityContainer>
              <PriorityNumber
                key={`${rule.id}-priority`}
                ref={(element) => setRef(element, `${rule.id}-priority`)}
              >
                {priorityForDisplay}
              </PriorityNumber>
              {coordinatesDefined && (
                <RuleJoiner
                  height="100%"
                  width="100px"
                  key={`${x1}${x2}${y1}${y2}`}
                >
                  <line
                    x1={x1}
                    x2={x2}
                    y1={y1}
                    y2={y2}
                    stroke={strokeColor}
                    strokeWidth="1"
                  />
                </RuleJoiner>
              )}
            </PriorityContainer>
            <RoutingRule
              rule={rule}
              groups={groups}
              index={index}
              defaultOpen={defaultOpen}
              attributes={attributes}
              onClick={openRule}
              recalculateCoordinates={recalculateCoordinates}
              saveRulesBeforeLeaving={saveRulesBeforeLeaving}
              onLeavePage={onLeavingRoutingRulePage}
              setAllRulesAreSaved={setAllRulesAreSaved}
              variant={variant}
            />
          </RuleContainer>
        </RuleListItem>
      )}
    </Draggable>
  );
};
